import { del, get, set } from 'idb-keyval';
import { migrate, ProjectFormatError } from '../core/model/serialize';
import type { EditorDocument } from '../core/model/types';

/**
 * Debounced autosave to IndexedDB, so a refresh or a crash does not lose work.
 *
 * The key/value store is injected: production uses idb-keyval, tests use an
 * in-memory map, and a browser without IndexedDB degrades to "no autosave"
 * instead of throwing on every keystroke.
 */

export interface KV {
  get<T = unknown>(key: string): Promise<T | undefined>;
  set<T = unknown>(key: string, value: T): Promise<void>;
  del(key: string): Promise<void>;
}

export const idbKv: KV = {
  get: <T>(key: string) => get<T>(key),
  set: <T>(key: string, value: T) => set(key, value),
  del: (key: string) => del(key),
};

export const AUTOSAVE_KEY = 'webpaint:autosave:v1';

export interface AutosaveSnapshot {
  doc: EditorDocument;
  savedAt: string;
}

export interface Autosaver {
  /** Mark the document dirty; the write happens after the debounce window. */
  schedule(): void;
  /** Write immediately (used on pagehide and in tests). */
  flush(): Promise<void>;
  load(): Promise<EditorDocument | null>;
  clear(): Promise<void>;
  start(): void;
  stop(): void;
  /** False when the backing store failed and autosave switched itself off. */
  isEnabled(): boolean;
}

export interface AutosaverOptions {
  getDoc: () => EditorDocument;
  kv?: KV;
  key?: string;
  debounceMs?: number;
  onError?: (error: unknown) => void;
}

export function createAutosaver(options: AutosaverOptions): Autosaver {
  const kv = options.kv ?? idbKv;
  const key = options.key ?? AUTOSAVE_KEY;
  const debounceMs = options.debounceMs ?? 800;

  let timer: ReturnType<typeof setTimeout> | undefined;
  let running = false;
  let enabled = true;

  const fail = (error: unknown) => {
    enabled = false;
    if (timer) clearTimeout(timer);
    options.onError?.(error);
  };

  const flush = async (): Promise<void> => {
    if (!enabled) return;
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
    try {
      const snapshot: AutosaveSnapshot = {
        doc: options.getDoc(),
        savedAt: new Date().toISOString(),
      };
      await kv.set(key, snapshot);
    } catch (error) {
      fail(error);
    }
  };

  return {
    schedule() {
      if (!enabled || !running) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void flush(), debounceMs);
    },

    flush,

    async load() {
      if (!enabled) return null;
      try {
        const snapshot = await kv.get<AutosaveSnapshot>(key);
        if (!snapshot || typeof snapshot !== 'object' || !('doc' in snapshot)) return null;
        // Snapshots go through the same door as project files: a schema bump
        // migrates old sessions instead of silently dropping them. A snapshot
        // that cannot be read is skipped — it must not reach the outer catch
        // and switch autosave off, which is for storage failures only.
        try {
          return migrate(snapshot.doc);
        } catch (error) {
          if (error instanceof ProjectFormatError) return null;
          throw error;
        }
      } catch (error) {
        fail(error);
        return null;
      }
    },

    async clear() {
      try {
        await kv.del(key);
      } catch (error) {
        fail(error);
      }
    },

    start() {
      running = true;
    },

    stop() {
      running = false;
      if (timer) clearTimeout(timer);
    },

    isEnabled() {
      return enabled;
    },
  };
}
